import { useAuth } from "@clerk/clerk-react";
import { Box, Skeleton } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect } from "react";

import { fetcherAuth } from "utils/api";
import { useGamification } from "utils/useGamification";

import CardHeader from "./components/CardHeader";
import CreateNicheTemplateCard from "./components/CreateNicheTemplateCard";
import CreateNicheTemplateCardLoading from "./components/CreateNicheTemplateCardLoading";
import WeeklyConsistencyChallenge from "./components/WeeklyConsistencyChallenge";
import WeeklyConsistencyChallengeLoading from "./components/WeeklyConssitencyChallengeLoading";
import {
  countCreditByTask,
  countTaskByType,
  displayFirstTemplateCard,
} from "./helpers";
import { OrganizationGamificationTask } from "./schema";
import styles from "./styles";

const Gamification = () => {
  const { getToken } = useAuth();
  const { currentOrg, gamificationProfile, credits, setCredits } = useContext(
    OrganizationUserContext,
  );
  const { setErrorAlert } = useContext(AlertContext);
  const { outreachTemplates, fetchOutreachTemplates } = useContext(
    OutreachTemplatesContext,
  );
  const { fetchGamificationProfile, profileLoading } = useGamification();

  const fetchCredits = async () => {
    try {
      const { credits } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/gamification/credits`,
      );
      setCredits(credits);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const isTaskCompleted = (
    taskType: OrganizationGamificationTask,
    index: number,
  ) => {
    if (!gamificationProfile) return false;
    return countTaskByType(gamificationProfile, taskType) >= index;
  };

  const computeNicheCardToRender = () => {
    if (!gamificationProfile) return 0;

    const tasksCompleted = countTaskByType(
      gamificationProfile,
      OrganizationGamificationTask.niche_templates,
    );
    const creditsCompleted = countCreditByTask(
      credits,
      OrganizationGamificationTask.niche_templates,
    );

    if (currentOrg?.isProUser) {
      if (
        tasksCompleted === 0 ||
        displayFirstTemplateCard(outreachTemplates, gamificationProfile)
      ) {
        return 1;
      }
      return 0;
    }

    if (currentOrg?.isVaUser) {
      if (creditsCompleted === 0 && tasksCompleted === 1) {
        return 1;
      }
      return 2;
    }
    return 0;
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchCredits();
      if (!gamificationProfile) fetchGamificationProfile();
      if (outreachTemplates?.length === 0) {
        fetchOutreachTemplates();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const totalCardsToRender = computeNicheCardToRender();

  if (profileLoading) {
    return (
      <Box sx={styles.paper}>
        {/* Card header skeleton */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Skeleton
            variant="rounded"
            width={48}
            height={48}
            sx={{ borderRadius: "10px" }}
          />
          <Box>
            <Skeleton variant="text" width={180} height={28} />
            <Skeleton variant="text" width={140} height={20} />
          </Box>
        </Box>
        <CreateNicheTemplateCardLoading />
        <WeeklyConsistencyChallengeLoading />
      </Box>
    );
  }

  const renderTemplateCard = (title = "Create a Niche-Specific Template") => {
    return (
      <CreateNicheTemplateCard
        title={title}
        description="Customize your pitch for a specific niche (e.g., beauty, fitness, tech)"
        completed={isTaskCompleted(
          OrganizationGamificationTask.niche_templates,
          1,
        )}
        infoBox={
          <>
            {" "}
            <strong>Pro tip:</strong> Different niches respond to different
            pitching styles. A beauty brand cares about aesthetics and
            engagement, while a tech brand wants to see expertise and audience
            demographics.
          </>
        }
      />
    );
  };

  return (
    <Box sx={styles.paper}>
      {currentOrg?.isProUser && (
        <>
          <CardHeader
            title="Pitching Challenges"
            description="Unlock Bento Site Credit!"
            icon="fa-solid fa-bullseye"
          />
          {totalCardsToRender > 0 && renderTemplateCard()}
          <WeeklyConsistencyChallenge
            completed={isTaskCompleted(
              OrganizationGamificationTask.email_streak,
              1,
            )}
          />
        </>
      )}
      {currentOrg?.isVaUser && totalCardsToRender > 0 && (
        <>
          <CardHeader
            title="Niche Templates Challenges"
            description="Earn credits for creating more templates"
            icon="fa-solid fa-pen"
          />
          {Array.from({ length: totalCardsToRender }).map((_, index) => (
            <CreateNicheTemplateCard
              key={index}
              title={
                totalCardsToRender <= 1
                  ? `Niche Template`
                  : `Niche Template #${index + 1}`
              }
              description={
                index === 0
                  ? "Create a pitch template for a specific niche (e.g., beauty, fitness, tech)"
                  : "Create a pitch template for a second niche to diversify your opportunities"
              }
              completed={
                countCreditByTask(
                  credits,
                  OrganizationGamificationTask.niche_templates,
                ) >=
                index + 1
              }
              infoBox={
                <>
                  {" "}
                  {index === 0 && (
                    <>
                      <strong>Pro tip:</strong> Different niches respond to
                      different pitching styles. A beauty brand cares about
                      aesthetics and engagement, while a tech brand wants to see
                      expertise and audience demographics.
                    </>
                  )}
                  {index === 1 && (
                    <>
                      <strong>Why a second niche?</strong> Having templates for
                      multiple niches increases your opportunities and helps you
                      understand which brand categories respond best to your
                      content style.
                    </>
                  )}
                </>
              }
            />
          ))}
        </>
      )}
    </Box>
  );
};

export default Gamification;
